import { getKromaticSiteOriginForHost } from 'helpers/common/common';

const DEFAULT_SITE_ORIGIN = 'https://kromatic.com';

function normalizeBasePath(basePath = process.env.NEXT_CONFIG_BASE_PATH || ''): string {
    if (!basePath) {
        return '';
    }

    const withLeadingSlash = basePath.startsWith('/') ? basePath : `/${basePath}`;
    const collapsed = withLeadingSlash.replace(/\/{2,}/g, '/');
    const trimmed = collapsed.endsWith('/') && collapsed !== '/' ? collapsed.slice(0, -1) : collapsed;
    return trimmed === '/' ? '' : trimmed;
}

function normalizeOrigin(origin?: string | null): string | null {
    if (!origin) {
        return null;
    }

    try {
        const parsed = new URL(origin);
        return `${parsed.protocol}//${parsed.host}`;
    } catch {
        return null;
    }
}

function normalizeRoutePath(routePath: string): string {
    const pathOnly = (routePath || '/').split(/[?#]/)[0] || '/';
    const withLeadingSlash = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
    return withLeadingSlash.replace(/\/{2,}/g, '/');
}

function stripBasePath(pathname: string, basePath: string): string {
    if (!basePath) {
        return pathname;
    }

    if (pathname === basePath) {
        return '/';
    }

    if (pathname.startsWith(`${basePath}/`)) {
        return pathname.slice(basePath.length);
    }

    return pathname;
}

export function getSeoOrigin(runtimeHostname?: string): string {
    const configuredOrigin = normalizeOrigin(process.env.NEXT_PUBLIC_SITE_URL);
    if (configuredOrigin) {
        return configuredOrigin;
    }

    const hostOrigin = getKromaticSiteOriginForHost(runtimeHostname);
    if (hostOrigin) {
        return hostOrigin;
    }

    if (typeof window !== 'undefined') {
        const windowOrigin = getKromaticSiteOriginForHost(window.location.hostname);
        if (windowOrigin) {
            return windowOrigin;
        }
    }

    return DEFAULT_SITE_ORIGIN;
}

export function getCanonicalUrl(routePath: string, runtimeHostname?: string): string {
    const origin = getSeoOrigin(runtimeHostname);
    const basePath = normalizeBasePath();
    const normalizedRoute = stripBasePath(normalizeRoutePath(routePath), basePath);
    const canonicalPath = `${basePath}${normalizedRoute === '/' ? '' : normalizedRoute}` || '/';
    return new URL(canonicalPath, `${origin}/`).toString();
}

export function toAbsoluteSeoUrl(url: string, runtimeHostname?: string): string {
    if (!url) {
        return url;
    }

    try {
        return new URL(url).toString();
    } catch {
        const normalizedPath = url.startsWith('/') ? url : `/${url}`;
        return new URL(normalizedPath, `${getSeoOrigin(runtimeHostname)}/`).toString();
    }
}
